import type { Contract, Property, User } from '@kalos/kalos-rpc';
import { Badge, LoadingIcon } from '@kalos/ui';
import { Building2, ChevronDown, ClipboardList, Home, UserIcon } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  CONTRACT_BADGE_CLASS,
  type ContractBadgeStatus,
} from '../../../../../tools/contractStatus';
import { formatDate } from '../../../../../tools/helpers';
// USER_CONTACTS DISABLED (temporary):
// import { UserContactsSection } from '../../../UserContacts';
import { useSchedulerStyle } from '../../context/schedulerStyle';

interface ReviewCustomerCardProps {
  customer: User;
  property: Property;
  contract: Contract | null;
  contractStatus: ContractBadgeStatus | null;
  isContractLoading: boolean;
}

export const ReviewCustomerCard = ({
  customer,
  property,
  contract,
  contractStatus,
  isContractLoading,
}: ReviewCustomerCardProps) => {
  const compact = useSchedulerStyle();
  const customerName = `${customer.firstname ?? ''} ${customer.lastname ?? ''}`.trim();
  const businessName = customer.businessname || '';

  const propertyCityStateZip = useMemo(
    () =>
      [property.city, property.state, property.zip]
        .filter(Boolean)
        .join(', ')
        .replace(/, (?=\d)/, ' '),
    [property.city, property.state, property.zip],
  );

  const fullAddress = [property.address, propertyCityStateZip].filter(Boolean).join(', ');

  const statusColor = contractStatus ? CONTRACT_BADGE_CLASS[contractStatus] : '';

  const contractDescription = useMemo(() => {
    if (!contract) return '';
    const parts: string[] = [];
    if (contract.notes) parts.push(contract.notes.split('\n')[0]);
    if (contract.dateEnded) parts.push(`Ends ${formatDate(contract.dateEnded)}`);
    return parts.join(' | ');
  }, [contract]);

  return (
    <div className={`rounded-lg border border-gray-200 bg-white ${compact ? 'p-4' : 'p-5'}`}>
      {/* Customer header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center bg-gray-100 ${
              compact ? 'rounded-full' : 'rounded-lg'
            }`}
          >
            {compact ? (
              <UserIcon className="h-5 w-5 text-gray-600" />
            ) : (
              <Building2 className="h-5 w-5 text-gray-600" />
            )}
          </div>
          <div>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <p
                className={`text-gray-900 ${compact ? 'text-sm font-semibold' : 'text-lg font-bold'}`}
              >
                {customerName}
              </p>
              {compact && businessName && (
                <span className="text-xs text-gray-500">· {businessName}</span>
              )}
              {customer.doNotServe && (
                <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-sm font-bold text-red-700">
                  DO NOT SERVE
                </span>
              )}
            </div>
            {!compact && businessName && <p className="text-sm text-gray-500">{businessName}</p>}
            {compact && (
              <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-500">
                <Home className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                <span>{fullAddress}</span>
              </div>
            )}
          </div>
        </div>

        {/* CTA buttons */}
        <div className="flex items-center gap-2">
          <a
            href={`/customers/${customer.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 ${compact ? 'text-xs' : 'text-sm'}`}
          >
            <UserIcon className="h-3.5 w-3.5" />
            Customer
          </a>
          <a
            href={`/customers/${customer.id}/properties/${property.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 ${compact ? 'text-xs' : 'text-sm'}`}
          >
            <Home className="h-3.5 w-3.5" />
            Property
          </a>
        </div>
      </div>

      {/* Contract row */}
      <ContractSection
        contract={contract}
        contractStatus={contractStatus}
        statusColor={statusColor}
        contractDescription={contractDescription}
        isContractLoading={isContractLoading}
        compact={compact}
      />

      {/* Property row (non-scheduler original layout) */}
      {!compact && (
        <div className="mt-3 flex items-center gap-3 px-4 py-2">
          <Home className="h-4 w-4 shrink-0 text-gray-400" />
          <div>
            <p className="text-sm font-medium text-gray-900">{property.address}</p>
            {propertyCityStateZip && (
              <p className="text-sm text-gray-500">{propertyCityStateZip}</p>
            )}
          </div>
        </div>
      )}

      {/* Contacts — USER_CONTACTS DISABLED (temporary):
      <UserContactsSection userId={customer.id} kind="email" className="mt-4 px-4" />
      <UserContactsSection userId={customer.id} kind="phone" className="mt-4 px-4 pb-2" /> */}
    </div>
  );
};

const ContractSection = ({
  contract,
  contractStatus,
  statusColor,
  contractDescription,
  isContractLoading,
  compact,
}: {
  contract: Contract | null;
  contractStatus: ContractBadgeStatus | null;
  statusColor: string;
  contractDescription: string;
  isContractLoading: boolean;
  compact: boolean;
}) => {
  const [expanded, setExpanded] = useState(false);

  if (isContractLoading) {
    return (
      <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <LoadingIcon className="size-4" />
          <span className="text-sm text-gray-400">Loading contract...</span>
        </div>
      </div>
    );
  }

  if (!contract) {
    return (
      <div className="mt-4 rounded-md border border-gray-100 bg-gray-50 px-4 py-3">
        <div className="flex items-center gap-3">
          <ClipboardList className="h-4 w-4 shrink-0 text-gray-400" />
          <span className="text-sm text-gray-500">No active contract</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-md border border-gray-100 bg-gray-50">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between px-4 py-3"
      >
        <div className="flex items-center gap-3">
          <ClipboardList className="h-4 w-4 shrink-0 text-gray-400" />
          <span
            className={`font-semibold ${compact ? 'text-xs text-gray-700' : 'text-sm text-gray-900'}`}
          >
            {contract.number || `C${contract.id}`}
          </span>
          <span className={compact ? 'text-xs text-gray-400' : 'text-sm text-gray-500'}>
            {contractDescription}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge
            className={`${statusColor} pointer-events-none rounded-full px-2.5 py-0.5 text-xs font-medium`}
          >
            {contractStatus}
          </Badge>
          <ChevronDown
            className={`h-3.5 w-3.5 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`}
          />
        </div>
      </button>
      {expanded && (
        <div className="border-t border-gray-100 px-4 py-3">
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {contract.dateStarted && (
              <div>
                <span className="text-gray-500">Start: </span>
                <span className="font-medium text-gray-900">
                  {formatDate(contract.dateStarted)}
                </span>
              </div>
            )}
            {contract.dateEnded && (
              <div>
                <span className="text-gray-500">End: </span>
                <span className="font-medium text-gray-900">{formatDate(contract.dateEnded)}</span>
              </div>
            )}
            {contract.frequency > 0 && (
              <div>
                <span className="text-gray-500">Frequency: </span>
                <span className="font-medium text-gray-900">{contract.frequency}x/year</span>
              </div>
            )}
            {contract.paymentType && (
              <div>
                <span className="text-gray-500">Payment: </span>
                <span className="font-medium text-gray-900">{contract.paymentType}</span>
              </div>
            )}
            {contract.paymentStatus && (
              <div>
                <span className="text-gray-500">Payment Status: </span>
                <span className="font-medium text-gray-900">{contract.paymentStatus}</span>
              </div>
            )}
          </div>
          {contract.notes && (
            <p className="mt-2 whitespace-pre-wrap text-sm text-gray-600">{contract.notes}</p>
          )}
        </div>
      )}
    </div>
  );
};
